import { useState, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Drawer,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  Slider,
  Collapse,
  useTheme
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import ScienceIcon from '@mui/icons-material/Science'
import EngineeringIcon from '@mui/icons-material/Engineering'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import StorageIcon from '@mui/icons-material/Storage'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {
  loadMeasurements,
  loadManifest,
  loadDocsIndex,
  getFilterOptions,
  getCorpusStats,
  formatMeasurement
} from '../services/corpusData'
import { Bar, Doughnut } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js'
import { ComparisonSparkline, EfficiencyIndicator } from '../components/ui/Sparkline'

// Register Chart.js elements needed for Doughnut charts
ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
)

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  )
}

// Measurement Detail Drawer with Industry Benchmarks
function MeasurementDetail({ measurement, open, onClose, allMeasurements = [] }) {
  if (!measurement) return null

  const m = measurement.raw || measurement

  // Calculate benchmarks
  const benchmarks = useMemo(() => {
    if (!allMeasurements.length) return null

    const getEfficiency = (m) => m.efficiencyPct || m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct
    const getSavings = (m) => m.annualSavings || m.financial?.annual_savings

    // Overall average
    const allEfficiencies = allMeasurements.map(getEfficiency).filter(Boolean)
    const overallAvgEff = allEfficiencies.length ? allEfficiencies.reduce((a, b) => a + b, 0) / allEfficiencies.length : null

    // Industry average
    const industryKey = measurement.industry?.toLowerCase().replace(/\s/g, '_')
    const industryMeasurements = allMeasurements.filter(m => {
      const ind = (m.industry || m.site?.industry || '').toLowerCase().replace(/\s/g, '_')
      return ind === industryKey
    })
    const industryEfficiencies = industryMeasurements.map(getEfficiency).filter(Boolean)
    const industryAvgEff = industryEfficiencies.length ? industryEfficiencies.reduce((a, b) => a + b, 0) / industryEfficiencies.length : null

    // Equipment type average
    const equipType = measurement.equipmentType?.toLowerCase()
    const equipMeasurements = allMeasurements.filter(m => {
      const eqType = (m.equipmentType || m.equipment?.[0]?.type || m.equipment?.type || '').toLowerCase()
      return eqType === equipType
    })
    const equipEfficiencies = equipMeasurements.map(getEfficiency).filter(Boolean)
    const equipAvgEff = equipEfficiencies.length ? equipEfficiencies.reduce((a, b) => a + b, 0) / equipEfficiencies.length : null

    // Savings benchmarks
    const allSavings = allMeasurements.map(getSavings).filter(Boolean)
    const avgSavings = allSavings.length ? allSavings.reduce((a, b) => a + b, 0) / allSavings.length : null

    return {
      overallAvgEff,
      industryAvgEff,
      industryCount: industryMeasurements.length,
      equipAvgEff,
      equipCount: equipMeasurements.length,
      avgSavings
    }
  }, [allMeasurements, measurement])

  const Section = ({ title, children }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
        {title}
      </Typography>
      {children}
    </Box>
  )

  const DetailRow = ({ label, value, icon }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon}
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value || 'N/A'}
      </Typography>
    </Box>
  )

  // Comparison component
  const BenchmarkComparison = ({ value, benchmark, label, count }) => {
    if (!value || !benchmark) return null
    const diff = value - benchmark
    const isAbove = diff > 0
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">{label} ({count} studies)</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{benchmark.toFixed(1)}%</Typography>
          <Chip
            size="small"
            label={`${isAbove ? '+' : ''}${diff.toFixed(1)}%`}
            color={isAbove ? 'success' : 'default'}
            sx={{ height: 20, '& .MuiChip-label': { px: 0.75 } }}
          />
          {isAbove ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="disabled" />}
        </Box>
      </Box>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: 480, p: 0 } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {measurement.id}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {measurement.source}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {/* Data Fields & Category Chips */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
          <Tooltip title="Percentage of key data fields populated (11 total)" arrow>
            <Chip
              label={`${measurement.dataFields}% Fields`}
              size="small"
              color={measurement.dataFields >= 90 ? 'success' : measurement.dataFields >= 60 ? 'warning' : 'default'}
            />
          </Tooltip>
          <Chip
            label={measurement.category?.replace(/_/g, ' ')}
            size="small"
            variant="outlined"
          />
        </Box>

        <Section title="LOCATION & INDUSTRY">
          <DetailRow label="Industry" value={measurement.industry} />
          <DetailRow label="State" value={measurement.state} />
          <DetailRow label="Test Date" value={measurement.testDate} />
        </Section>

        <Divider sx={{ my: 2 }} />

        <Section title="EQUIPMENT DETAILS">
          <DetailRow label="Type" value={measurement.equipmentType} icon={<EngineeringIcon fontSize="small" />} />
          <DetailRow label="Age" value={measurement.equipmentAge ? `${measurement.equipmentAge} years` : null} />
          <DetailRow label="Capacity" value={measurement.capacity ? `${measurement.capacity} tons` : null} />
          <DetailRow label="Refrigerant" value={measurement.refrigerant} />
        </Section>

        <Divider sx={{ my: 2 }} />

        <Section title="PERFORMANCE METRICS">
          <DetailRow
            label="Efficiency Improvement"
            value={measurement.efficiencyPct ? `${measurement.efficiencyPct.toFixed(1)}%` : null}
            icon={<TrendingUpIcon fontSize="small" color="success" />}
          />
          <DetailRow label="kW/ton Before" value={measurement.kwPerTonBefore?.toFixed(3)} />
          <DetailRow label="kW/ton After" value={measurement.kwPerTonAfter?.toFixed(3)} />
          {measurement.kwPerTonBefore && measurement.kwPerTonAfter && (
            <DetailRow
              label="kW/ton Reduction"
              value={(measurement.kwPerTonBefore - measurement.kwPerTonAfter).toFixed(3)}
            />
          )}
          <DetailRow label="COP" value={measurement.cop?.toFixed(2)} />
        </Section>

        {/* Benchmark Comparison Section */}
        {benchmarks && measurement.efficiencyPct && (
          <>
            <Divider sx={{ my: 2 }} />
            <Section title="BENCHMARK COMPARISON">
              <Box sx={{ mb: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  This measurement: <span style={{ color: isDark ? '#A0CE4E' : '#1a1a1a' }}>{measurement.efficiencyPct.toFixed(1)}%</span> efficiency
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  vs. industry benchmarks:
                </Typography>
              </Box>
              <BenchmarkComparison
                value={measurement.efficiencyPct}
                benchmark={benchmarks.overallAvgEff}
                label="Overall Average"
                count={allMeasurements.length}
              />
              {benchmarks.industryAvgEff && (
                <BenchmarkComparison
                  value={measurement.efficiencyPct}
                  benchmark={benchmarks.industryAvgEff}
                  label={`${measurement.industry || 'Industry'} Avg`}
                  count={benchmarks.industryCount}
                />
              )}
              {benchmarks.equipAvgEff && (
                <BenchmarkComparison
                  value={measurement.efficiencyPct}
                  benchmark={benchmarks.equipAvgEff}
                  label={`${measurement.equipmentType || 'Equipment'} Avg`}
                  count={benchmarks.equipCount}
                />
              )}
            </Section>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Section title="FINANCIAL">
          <DetailRow
            label="Annual Savings"
            value={measurement.annualSavings ? `$${measurement.annualSavings.toLocaleString()}` : null}
            icon={<AttachMoneyIcon fontSize="small" />}
          />
          <DetailRow label="Payback Period" value={measurement.paybackMonths ? `${measurement.paybackMonths} months` : null} />
          <DetailRow label="Treatment Cost" value={measurement.treatmentCost ? `$${measurement.treatmentCost.toLocaleString()}` : null} />
          {benchmarks?.avgSavings && measurement.annualSavings && (
            <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Avg savings across corpus: ${benchmarks.avgSavings.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </Typography>
            </Box>
          )}
        </Section>

        <Divider sx={{ my: 2 }} />

        <Section title="CERTIFICATION">
          <DetailRow label="PE Name" value={measurement.peName} icon={<ScienceIcon fontSize="small" />} />
          <DetailRow label="Methodology" value={measurement.methodology} />
        </Section>

        {/* Raw Data Toggle */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="caption" color="text.secondary">
            Raw JSON available for export
          </Typography>
        </Box>
      </Box>
    </Drawer>
  )
}

// Statistics Tab Component
function StatisticsTab({ stats, measurements, loading }) {
  const [activeView, setActiveView] = useState('overview')
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
  }

  if (!stats) return null

  // Calculate equipment type breakdown from measurements
  const equipmentBreakdown = useMemo(() => {
    const byType = {}
    measurements.forEach(m => {
      const type = m.equipment?.[0]?.type || (Array.isArray(m.equipment) ? null : m.equipment?.type) || 'Unknown'
      if (!byType[type]) {
        byType[type] = { count: 0, efficiencies: [], savings: [] }
      }
      byType[type].count++
      const eff = m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct
      if (eff) byType[type].efficiencies.push(eff)
      if (m.financial?.annual_savings) byType[type].savings.push(m.financial.annual_savings)
    })
    return Object.entries(byType).map(([type, data]) => ({
      type,
      count: data.count,
      avgEfficiency: data.efficiencies.length ? (data.efficiencies.reduce((a, b) => a + b, 0) / data.efficiencies.length).toFixed(1) : null,
      totalSavings: data.savings.reduce((a, b) => a + b, 0)
    })).sort((a, b) => b.count - a.count)
  }, [measurements])

  // Calculate industry breakdown
  const industryBreakdown = useMemo(() => {
    const byIndustry = {}
    measurements.forEach(m => {
      const industry = m.site?.industry || 'Unknown'
      if (!byIndustry[industry]) {
        byIndustry[industry] = { count: 0, efficiencies: [], savings: [] }
      }
      byIndustry[industry].count++
      const eff = m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct
      if (eff) byIndustry[industry].efficiencies.push(eff)
      if (m.financial?.annual_savings) byIndustry[industry].savings.push(m.financial.annual_savings)
    })
    return Object.entries(byIndustry).map(([industry, data]) => ({
      industry: industry.replace(/_/g, ' '),
      count: data.count,
      avgEfficiency: data.efficiencies.length ? (data.efficiencies.reduce((a, b) => a + b, 0) / data.efficiencies.length).toFixed(1) : null,
      totalSavings: data.savings.reduce((a, b) => a + b, 0)
    })).sort((a, b) => b.count - a.count).slice(0, 10)
  }, [measurements])

  // Calculate geographic breakdown
  const stateBreakdown = useMemo(() => {
    const byState = {}
    measurements.forEach(m => {
      const state = m.site?.state || m.state || 'Unknown'
      if (!byState[state]) byState[state] = 0
      byState[state]++
    })
    return Object.entries(byState).sort((a, b) => b[1] - a[1]).slice(0, 12)
  }, [measurements])

  // Field completeness (per-field analysis)
  const fieldCompleteness = useMemo(() => {
    const fields = {
      'Equipment Type': 0,
      'Industry': 0,
      'State': 0,
      'Efficiency %': 0,
      'kW/ton Before': 0,
      'kW/ton After': 0,
      'Annual Savings': 0,
      'Payback Period': 0,
      'PE Certification': 0
    }
    const total = measurements.length
    measurements.forEach(m => {
      if (m.equipment?.[0]?.type || m.equipment?.type) fields['Equipment Type']++
      if (m.site?.industry) fields['Industry']++
      if (m.site?.state || m.state) fields['State']++
      if (m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct) fields['Efficiency %']++
      if (m.measurements?.kw_per_ton?.before) fields['kW/ton Before']++
      if (m.measurements?.kw_per_ton?.after) fields['kW/ton After']++
      if (m.financial?.annual_savings) fields['Annual Savings']++
      if (m.financial?.payback_months) fields['Payback Period']++
      if (m.certification?.pe_name || m.pe_name) fields['PE Certification']++
    })
    return Object.entries(fields).map(([field, count]) => ({
      field,
      count,
      percent: ((count / total) * 100).toFixed(0)
    }))
  }, [measurements])

  // Data completeness distribution (by percentage ranges)
  const completenessDistribution = useMemo(() => {
    // Calculate field completeness for each record
    const getFieldCount = (m) => {
      let count = 0
      const eq = m.equipment?.[0] || m.equipment || {}
      if (m.site?.industry) count++
      if (m.site?.state || m.state) count++
      if (eq.type) count++
      if (eq.age_years) count++
      if (eq.capacity_tons) count++
      if (eq.refrigerant) count++
      if (m.measurements?.efficiency_improvement_pct || m.measurements?.kw_per_ton?.improvement_pct) count++
      if (m.measurements?.kw_per_ton?.before) count++
      if (m.measurements?.kw_per_ton?.after) count++
      if (m.financial?.annual_savings) count++
      if (m.certification?.pe_name || m.pe_name) count++
      return Math.round((count / 11) * 100)
    }

    const percentages = measurements.map(getFieldCount)
    const high = percentages.filter(p => p >= 90).length
    const medium = percentages.filter(p => p >= 60 && p < 90).length
    const low = percentages.filter(p => p < 60).length
    const avgCompleteness = percentages.length > 0
      ? (percentages.reduce((a, b) => a + b, 0) / percentages.length).toFixed(0)
      : 0

    return {
      high,
      medium,
      low,
      avgCompleteness,
      total: measurements.length
    }
  }, [measurements])

  // Data completeness distribution chart data
  const completenessChartData = {
    labels: ['90%+ Complete', '60-89% Partial', '<60% Minimal'],
    datasets: [{
      data: [completenessDistribution.high, completenessDistribution.medium, completenessDistribution.low],
      backgroundColor: ['#4CAF50', '#FFC107', '#9E9E9E'],
      borderWidth: 0
    }]
  }

  const categoryData = {
    labels: Object.keys(stats.categoryBreakdown || {}).map(c => c.replace(/_/g, ' ')),
    datasets: [{
      data: Object.values(stats.categoryBreakdown || {}),
      backgroundColor: ['#2196F3', '#9C27B0', '#FF5722', '#607D8B'],
      borderWidth: 0
    }]
  }

  const equipmentChartData = {
    labels: equipmentBreakdown.slice(0, 6).map(e => e.type),
    datasets: [{
      label: 'Case Count',
      data: equipmentBreakdown.slice(0, 6).map(e => e.count),
      backgroundColor: '#A0CE4E',
      borderRadius: 4
    }]
  }

  const industryChartData = {
    labels: industryBreakdown.slice(0, 8).map(i => i.industry),
    datasets: [{
      label: 'Case Count',
      data: industryBreakdown.slice(0, 8).map(i => i.count),
      backgroundColor: '#64b5f6',
      borderRadius: 4
    }]
  }

  return (
    <Box>
      {/* View Toggle */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
        <Chip label="Overview" onClick={() => setActiveView('overview')} color={activeView === 'overview' ? 'primary' : 'default'} />
        <Chip label="Equipment Analysis" onClick={() => setActiveView('equipment')} color={activeView === 'equipment' ? 'primary' : 'default'} />
        <Chip label="Industry Analysis" onClick={() => setActiveView('industry')} color={activeView === 'industry' ? 'primary' : 'default'} />
        <Chip label="Data Quality" onClick={() => setActiveView('quality')} color={activeView === 'quality' ? 'primary' : 'default'} />
      </Box>

      {activeView === 'overview' && (
        <Grid container spacing={3}>
          {/* Summary Stats */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">{stats.totalRecords}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Records</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {stats.efficiencyStats?.mean?.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Mean Efficiency</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main">
                    ${(stats.financialStats?.totalSavings / 1000000)?.toFixed(2)}M
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Total Savings</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main">
                    {stats.ageStats?.mean?.toFixed(1)} yrs
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Avg Equipment Age</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary.main">
                    {stateBreakdown.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">States Covered</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Data Field Completeness</Typography>
                <Box sx={{ height: 250 }}>
                  <Doughnut data={completenessChartData} options={{ maintainAspectRatio: false }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Record Categories</Typography>
                <Box sx={{ height: 250 }}>
                  <Doughnut data={categoryData} options={{ maintainAspectRatio: false }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Efficiency Range */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Efficiency Improvement Range</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, bgcolor: 'error.lighter', textAlign: 'center' }}>
                      <Typography variant="h5">{stats.efficiencyStats?.min?.toFixed(1)}%</Typography>
                      <Typography variant="caption">Minimum</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, bgcolor: 'success.lighter', textAlign: 'center' }}>
                      <Typography variant="h5">{stats.efficiencyStats?.mean?.toFixed(1)}%</Typography>
                      <Typography variant="caption">Mean</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.lighter', textAlign: 'center' }}>
                      <Typography variant="h5">{stats.efficiencyStats?.max?.toFixed(1)}%</Typography>
                      <Typography variant="caption">Maximum</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeView === 'equipment' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Equipment Type Distribution</Typography>
                <Box sx={{ height: 300 }}>
                  <Bar data={equipmentChartData} options={{ indexAxis: 'y', maintainAspectRatio: false }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Equipment Performance Summary</Typography>
                {equipmentBreakdown.map(eq => (
                  <Box key={eq.type} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{eq.type}</Typography>
                      <Typography variant="caption" color="text.secondary">{eq.count} case studies</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        {eq.avgEfficiency ? `${eq.avgEfficiency}% avg` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ${eq.totalSavings.toLocaleString()} total savings
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeView === 'industry' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Industry Distribution</Typography>
                <Box sx={{ height: 350 }}>
                  <Bar data={industryChartData} options={{ indexAxis: 'y', maintainAspectRatio: false }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Industry Performance Summary</Typography>
                {industryBreakdown.map(ind => (
                  <Box key={ind.industry} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>{ind.industry}</Typography>
                      <Typography variant="caption" color="text.secondary">{ind.count} case studies</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        {ind.avgEfficiency ? `${ind.avgEfficiency}% avg` : 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ${ind.totalSavings.toLocaleString()} total savings
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          {/* Geographic Coverage */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Geographic Coverage (Top States)</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {stateBreakdown.map(([state, count]) => (
                    <Chip key={state} label={`${state}: ${count}`} variant="outlined" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeView === 'quality' && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Field Completeness by Type</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Percentage of records with each field populated
                </Typography>
                {fieldCompleteness.map(({ field, count, percent }) => (
                  <Box key={field} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{field}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{percent}%</Typography>
                    </Box>
                    <Box sx={{ height: 8, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
                      <Box sx={{
                        width: `${percent}%`,
                        height: '100%',
                        bgcolor: parseInt(percent) >= 80 ? 'success.main' : parseInt(percent) >= 50 ? 'warning.main' : 'error.main',
                        borderRadius: 1
                      }} />
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Data Field Completeness Distribution</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Records grouped by % of key data fields populated (11 fields tracked)
                </Typography>
                <Box sx={{ height: 250 }}>
                  <Doughnut data={completenessChartData} options={{ maintainAspectRatio: false }} />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                    Average Completeness: <span style={{ color: isDark ? '#4CAF50' : '#1a1a1a' }}>{completenessDistribution.avgCompleteness}%</span>
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.lighter' }}>
                      <Typography variant="h5">{completenessDistribution.high}</Typography>
                      <Typography variant="caption">90%+ Complete</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'warning.lighter' }}>
                      <Typography variant="h5">{completenessDistribution.medium}</Typography>
                      <Typography variant="caption">60-89% Partial</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'grey.100' }}>
                      <Typography variant="h5">{completenessDistribution.low}</Typography>
                      <Typography variant="caption">&lt;60% Minimal</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

// Document Viewer Modal
function DocumentViewer({ doc, open, onClose }) {
  if (!doc) return null

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', md: 600 }, p: 0 } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'info.main', color: 'white', display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {doc.slug}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {doc.title || 'Document Details'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        {/* Document Type & Category */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={doc.category || 'Unknown'} color="primary" size="small" />
          <Chip label={doc.doc_type || 'document'} variant="outlined" size="small" />
          {doc.pages && <Chip label={`${doc.pages} pages`} variant="outlined" size="small" />}
        </Box>

        {/* Metadata Section */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="primary" gutterBottom>DOCUMENT METADATA</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Slug</Typography>
                <Typography variant="body2">{doc.slug}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Category</Typography>
                <Typography variant="body2">{doc.category?.replace(/_/g, ' ') || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Document Type</Typography>
                <Typography variant="body2">{doc.doc_type || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Pages</Typography>
                <Typography variant="body2">{doc.pages || 'N/A'}</Typography>
              </Grid>
              {doc.token_count && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Token Count</Typography>
                  <Typography variant="body2">{doc.token_count.toLocaleString()}</Typography>
                </Grid>
              )}
              {doc.chunk_count && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Chunks</Typography>
                  <Typography variant="body2">{doc.chunk_count}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* File Paths */}
        {doc.files && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="primary" gutterBottom>FILE PATHS</Typography>
              <Box sx={{ fontFamily: 'monospace', fontSize: 12, bgcolor: 'action.hover', p: 1.5, borderRadius: 1, maxHeight: 150, overflow: 'auto' }}>
                {Object.entries(doc.files || {}).map(([key, path]) => (
                  <Box key={key} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="primary" component="span">{key}:</Typography>
                    <Typography variant="caption" component="span" sx={{ ml: 1, wordBreak: 'break-all' }}>{path}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Study Information if available */}
        {doc.study_id && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" color="primary" gutterBottom>STUDY INFORMATION</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Study ID</Typography>
                  <Typography variant="body2">{doc.study_id}</Typography>
                </Grid>
                {doc.industry && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Industry</Typography>
                    <Typography variant="body2">{doc.industry.replace(/_/g, ' ')}</Typography>
                  </Grid>
                )}
                {doc.state && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">State</Typography>
                    <Typography variant="body2">{doc.state}</Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Raw Data */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="primary" gutterBottom>RAW JSON DATA</Typography>
            <Box sx={{ fontFamily: 'monospace', fontSize: 11, bgcolor: 'grey.900', color: 'grey.100', p: 1.5, borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(doc, null, 2)}
              </pre>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Drawer>
  )
}

// Documents Tab Component
function DocumentsTab({ docs, loading }) {
  const [search, setSearch] = useState('')
  const [selectedDoc, setSelectedDoc] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState('all')

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
  }

  const docList = Object.entries(docs || {}).map(([slug, doc]) => ({
    id: slug,
    slug,
    ...doc
  }))

  // Get unique categories
  const categories = [...new Set(docList.map(d => d.category).filter(Boolean))]

  const filtered = docList.filter(d => {
    const matchesSearch = search === '' ||
      d.slug.toLowerCase().includes(search.toLowerCase()) ||
      d.title?.toLowerCase().includes(search.toLowerCase()) ||
      d.category?.toLowerCase().includes(search.toLowerCase())
    const matchesCategory = categoryFilter === 'all' || d.category === categoryFilter
    return matchesSearch && matchesCategory
  })

  const columns = [
    { field: 'slug', headerName: 'Document', flex: 1, minWidth: 200 },
    {
      field: 'category',
      headerName: 'Category',
      width: 150,
      renderCell: (params) => params.value ? (
        <Chip label={params.value.replace(/_/g, ' ')} size="small" variant="outlined" />
      ) : 'N/A'
    },
    { field: 'doc_type', headerName: 'Type', width: 120 },
    { field: 'pages', headerName: 'Pages', width: 80, type: 'number' },
    {
      field: 'actions',
      headerName: 'View',
      width: 80,
      renderCell: (params) => (
        <Button size="small" onClick={() => setSelectedDoc(params.row)}>View</Button>
      )
    }
  ]

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search documents..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Category</InputLabel>
          <Select value={categoryFilter} label="Category" onChange={(e) => setCategoryFilter(e.target.value)}>
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map(cat => (
              <MenuItem key={cat} value={cat}>{cat.replace(/_/g, ' ')}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {filtered.length} of {docList.length} documents
      </Typography>

      <DataGrid
        rows={filtered}
        columns={columns}
        autoHeight
        pageSize={15}
        rowsPerPageOptions={[10, 15, 25, 50]}
        disableSelectionOnClick
        density="compact"
        onRowClick={(params) => setSelectedDoc(params.row)}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }
        }}
      />

      {/* Document Viewer */}
      <DocumentViewer
        doc={selectedDoc}
        open={Boolean(selectedDoc)}
        onClose={() => setSelectedDoc(null)}
      />
    </Box>
  )
}

// Main Component
function DataExplorer() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Data state
  const [measurements, setMeasurements] = useState([])
  const [filterOptions, setFilterOptions] = useState({})
  const [stats, setStats] = useState(null)
  const [docs, setDocs] = useState({})

  // Filter state
  const [filters, setFilters] = useState({
    equipmentType: searchParams.get('equipment') || 'all',
    industry: searchParams.get('industry') || 'all',
    state: searchParams.get('state') || 'all',
    refrigerant: searchParams.get('refrigerant') || 'all',
    ageRange: searchParams.get('ageRange') || 'all',
    efficiencyRange: searchParams.get('efficiencyRange') || 'all',
    category: 'all',
    search: ''
  })

  // Selected measurement for detail view
  const [selectedMeasurement, setSelectedMeasurement] = useState(null)


  // Global search (across all tabs)
  const [globalSearch, setGlobalSearch] = useState('')

  // Efficiency range slider
  const [efficiencySliderRange, setEfficiencySliderRange] = useState([0, 100])

  // Advanced filters expanded state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(true)

  // Load data on mount
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true)
        const [measurementsData, options, statsData, docsData] = await Promise.all([
          loadMeasurements(),
          getFilterOptions(),
          getCorpusStats(),
          loadDocsIndex()
        ])

        setMeasurements(measurementsData)
        setFilterOptions(options)
        setStats(statsData)
        setDocs(docsData)
        setError(null)
      } catch (err) {
        console.error('Failed to load corpus data:', err)
        setError(`Failed to load corpus data: ${err.message}. Check browser console for details.`)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  // Filter and format measurements
  const filteredMeasurements = useMemo(() => {
    let result = measurements.map(formatMeasurement)

    // Apply filters
    if (filters.equipmentType !== 'all') {
      result = result.filter(m => m.equipmentType?.toLowerCase() === filters.equipmentType.toLowerCase())
    }
    if (filters.industry !== 'all') {
      result = result.filter(m => m.industry?.toLowerCase().replace(/\s/g, '_') === filters.industry.toLowerCase())
    }
    if (filters.state !== 'all') {
      result = result.filter(m => m.state === filters.state)
    }
    if (filters.category !== 'all') {
      result = result.filter(m => m.category === filters.category)
    }
    if (filters.refrigerant !== 'all') {
      result = result.filter(m => m.refrigerant === filters.refrigerant)
    }
    if (filters.ageRange !== 'all') {
      // Parse age range like "0_to_5_years" or "over_20_years"
      const ageRange = filters.ageRange
      result = result.filter(m => {
        const age = m.equipmentAge
        if (age == null) return false
        if (ageRange === '0_to_5_years') return age >= 0 && age <= 5
        if (ageRange === '5_to_10_years') return age > 5 && age <= 10
        if (ageRange === '10_to_15_years') return age > 10 && age <= 15
        if (ageRange === '15_to_20_years') return age > 15 && age <= 20
        if (ageRange === 'over_20_years') return age > 20
        return true
      })
    }
    if (filters.efficiencyRange !== 'all') {
      // Parse efficiency range like "20-25"
      const [min, max] = filters.efficiencyRange.split('-').map(Number)
      result = result.filter(m => {
        const eff = m.efficiencyPct
        if (eff == null) return false
        return eff >= min && eff < max
      })
    }
    if (filters.search) {
      const search = filters.search.toLowerCase()
      result = result.filter(m =>
        m.id?.toLowerCase().includes(search) ||
        m.source?.toLowerCase().includes(search) ||
        m.industry?.toLowerCase().includes(search) ||
        m.peName?.toLowerCase().includes(search)
      )
    }

    // Apply global search (searches more fields)
    if (globalSearch) {
      const search = globalSearch.toLowerCase()
      result = result.filter(m =>
        m.id?.toLowerCase().includes(search) ||
        m.source?.toLowerCase().includes(search) ||
        m.industry?.toLowerCase().includes(search) ||
        m.state?.toLowerCase().includes(search) ||
        m.equipmentType?.toLowerCase().includes(search) ||
        m.peName?.toLowerCase().includes(search) ||
        m.refrigerant?.toLowerCase().includes(search) ||
        m.methodology?.toLowerCase().includes(search)
      )
    }

    // Apply efficiency slider range (only if not at default values)
    if (efficiencySliderRange[0] > 0 || efficiencySliderRange[1] < 100) {
      result = result.filter(m => {
        const eff = m.efficiencyPct
        if (eff == null) return false
        return eff >= efficiencySliderRange[0] && eff <= efficiencySliderRange[1]
      })
    }

    return result
  }, [measurements, filters, globalSearch, efficiencySliderRange])

  // DataGrid columns
  const columns = [
    { field: 'id', headerName: 'ID', width: 100 },
    { field: 'equipmentType', headerName: 'Equipment', width: 100 },
    {
      field: 'industry',
      headerName: 'Industry',
      width: 170,
      flex: 0.8,
      minWidth: 130,
      renderCell: (params) => (
        <Tooltip title={params.value?.replace(/_/g, ' ') || ''} arrow>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
            {params.value?.replace(/_/g, ' ') || '-'}
          </Box>
        </Tooltip>
      )
    },
    { field: 'state', headerName: 'State', width: 70 },
    {
      field: 'efficiencyPct',
      headerName: 'Efficiency Improvement (%)',
      width: 150,
      type: 'number',
      renderCell: (params) => params.value ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EfficiencyIndicator value={params.value} width={50} height={8} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value.toFixed(1)}%
          </Typography>
        </Box>
      ) : 'N/A'
    },
    {
      field: 'kwTonComparison',
      headerName: 'kW/Ton Change',
      width: 140,
      renderCell: (params) => {
        const before = params.row.kwPerTonBefore
        const after = params.row.kwPerTonAfter
        if (!before || !after) return 'N/A'
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ComparisonSparkline
              before={before}
              after={after}
              width={60}
              height={24}
            />
            <Typography variant="caption" color="text.secondary">
              {before.toFixed(2)}→{after.toFixed(2)}
            </Typography>
          </Box>
        )
      }
    },
    {
      field: 'annualSavings',
      headerName: 'Annual Savings',
      width: 120,
      type: 'number',
      renderCell: (params) => params.value ? (
        <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 500 }}>
          ${params.value.toLocaleString()}
        </Typography>
      ) : 'N/A'
    },
    { field: 'peName', headerName: 'Certified By', width: 180 }
  ]

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    setFilters({
      equipmentType: 'all',
      industry: 'all',
      state: 'all',
      refrigerant: 'all',
      ageRange: 'all',
      efficiencyRange: 'all',
      category: 'all',
      search: ''
    })
    setGlobalSearch('')
  }

  const activeFilterCount = Object.values(filters).filter(v => v && v !== 'all' && v !== '').length

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Data Explorer
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Explore corpus training data: {stats?.totalRecords || 0} case studies, 254 measurements
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Global Search */}
          <TextField
            size="small"
            placeholder="Search all tabs..."
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
          />

          {/* Selection indicator */}
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Measurements" />
          <Tab label="Documents" />
          <Tab label="Statistics" />
        </Tabs>

        <CardContent sx={{ p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {/* Measurements Tab */}
          <TabPanel value={tab} index={0}>
            {/* Filters */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  sx={{ minWidth: 200 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Equipment</InputLabel>
                  <Select
                    value={filters.equipmentType}
                    label="Equipment"
                    onChange={(e) => handleFilterChange('equipmentType', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {filterOptions.equipmentTypes?.map(t => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Industry</InputLabel>
                  <Select
                    value={filters.industry}
                    label="Industry"
                    onChange={(e) => handleFilterChange('industry', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {filterOptions.industries?.map(i => (
                      <MenuItem key={i} value={i}>{i.replace(/_/g, ' ')}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={filters.state}
                    label="State"
                    onChange={(e) => handleFilterChange('state', e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    {filterOptions.states?.map(s => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {activeFilterCount > 0 && (
                  <Button
                    size="small"
                    onClick={clearFilters}
                    startIcon={<RefreshIcon />}
                  >
                    Clear ({activeFilterCount})
                  </Button>
                )}

                <Button
                  size="small"
                  onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                  endIcon={showAdvancedFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  variant="text"
                >
                  Advanced
                </Button>
              </Box>

              {/* Advanced Filters (Collapsible) */}
              <Collapse in={showAdvancedFilters}>
                <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Efficiency Range Filter
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                      {efficiencySliderRange[0]}%
                    </Typography>
                    <Slider
                      value={efficiencySliderRange}
                      onChange={(e, newValue) => setEfficiencySliderRange(newValue)}
                      valueLabelDisplay="auto"
                      min={0}
                      max={100}
                      marks={[
                        { value: 0, label: '0%' },
                        { value: 25, label: '25%' },
                        { value: 50, label: '50%' },
                        { value: 75, label: '75%' },
                        { value: 100, label: '100%' }
                      ]}
                      sx={{ flex: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                      {efficiencySliderRange[1]}%
                    </Typography>
                  </Box>
                  {(efficiencySliderRange[0] > 0 || efficiencySliderRange[1] < 100) && (
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label={`Efficiency: ${efficiencySliderRange[0]}% - ${efficiencySliderRange[1]}%`}
                        size="small"
                        color="primary"
                        onDelete={() => setEfficiencySliderRange([0, 100])}
                      />
                    </Box>
                  )}
                </Box>
              </Collapse>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Showing {filteredMeasurements.length} of {measurements.length} records
              </Typography>
            </Box>

            {/* Data Grid */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={filteredMeasurements}
                columns={columns}
                autoHeight
                pageSize={25}
                rowsPerPageOptions={[10, 25, 50, 100]}
                disableRowSelectionOnClick
                onRowClick={(params) => setSelectedMeasurement(params.row)}
                sx={{
                  '& .MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                    lineHeight: 1.2,
                    textAlign: 'center'
                  },
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                  }
                }}
              />
            )}
          </TabPanel>

          {/* Documents Tab */}
          <TabPanel value={tab} index={1}>
            <DocumentsTab docs={docs} loading={loading} />
          </TabPanel>

          {/* Statistics Tab */}
          <TabPanel value={tab} index={2}>
            <StatisticsTab stats={stats} measurements={measurements} loading={loading} />
          </TabPanel>

        </CardContent>
      </Card>

      {/* Measurement Detail Drawer */}
      <MeasurementDetail
        measurement={selectedMeasurement}
        open={Boolean(selectedMeasurement)}
        onClose={() => setSelectedMeasurement(null)}
        allMeasurements={measurements}
      />
    </Box>
  )
}

export default DataExplorer
